import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Loader2, Plus, Trash2, Save, Rss } from "lucide-react";
import {
  listRssSources,
  upsertRssSource,
  deleteRssSource,
} from "@/lib/api/rss-admin.functions";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/rss-sources",
)({
  component: RssSourcesPage,
});

interface SourceDraft {
  id?: string;
  name: string;
  url: string;
  default_category: string;
  enabled: boolean;
  frequency_minutes: number;
}

const BLANK: SourceDraft = {
  name: "",
  url: "",
  default_category: "general",
  enabled: true,
  frequency_minutes: 60,
};

function RssSourcesPage() {
  const listFn = useServerFn(listRssSources);
  const upsertFn = useServerFn(upsertRssSource);
  const deleteFn = useServerFn(deleteRssSource);
  const qc = useQueryClient();

  const [draft, setDraft] = useState<SourceDraft>(BLANK);
  const [editing, setEditing] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["rss-sources"],
    queryFn: () => listFn(),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["rss-sources"] });

  const save = useMutation({
    mutationFn: (d: SourceDraft) => upsertFn({ data: d }),
    onSuccess: () => {
      setDraft(BLANK);
      setEditing(null);
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: invalidate,
  });

  function startEdit(s: any) {
    setEditing(s.id);
    setDraft({
      id: s.id,
      name: s.name,
      url: s.url,
      default_category: s.default_category ?? "general",
      enabled: !!s.enabled,
      frequency_minutes: s.frequency_minutes ?? 60,
    });
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  return (
    <div className="container-narrow space-y-8 py-10">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">
          Admin · Ingestion
        </p>
        <h1 className="mt-1 font-display text-3xl text-navy">RSS sources</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Manage which feeds (eNCA, SABC, …) the news ingester pulls from.
          Per-source frequency controls how often the cron worker calls them.
        </p>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(draft);
        }}
        className="space-y-3 rounded-2xl border border-border bg-card p-4 shadow-soft"
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs font-medium text-foreground/80">
            Name
            <input
              required
              value={draft.name}
              onChange={(e) =>
                setDraft((d) => ({ ...d, name: e.target.value }))
              }
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            />
          </label>
          <label className="text-xs font-medium text-foreground/80">
            Feed URL
            <input
              required
              type="url"
              value={draft.url}
              onChange={(e) =>
                setDraft((d) => ({ ...d, url: e.target.value }))
              }
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            />
          </label>
          <label className="text-xs font-medium text-foreground/80">
            Default category
            <input
              value={draft.default_category}
              onChange={(e) =>
                setDraft((d) => ({ ...d, default_category: e.target.value }))
              }
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            />
          </label>
          <label className="text-xs font-medium text-foreground/80">
            Frequency (minutes, 15–1440)
            <input
              type="number"
              min={15}
              max={1440}
              value={draft.frequency_minutes}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  frequency_minutes: Number(e.target.value) || 60,
                }))
              }
              className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            />
          </label>
        </div>
        <label className="inline-flex items-center gap-2 text-xs font-medium text-foreground/80">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(e) =>
              setDraft((d) => ({ ...d, enabled: e.target.checked }))
            }
          />
          Enabled
        </label>
        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={save.isPending}
            className="inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {editing ? <Save className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            {editing ? "Update source" : "Add source"}
          </button>
          {editing && (
            <button
              type="button"
              onClick={() => {
                setDraft(BLANK);
                setEditing(null);
              }}
              className="text-xs text-muted-foreground hover:underline"
            >
              Cancel edit
            </button>
          )}
          {save.error && (
            <span className="text-xs text-rose-700">
              {(save.error as Error).message}
            </span>
          )}
        </div>
      </form>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading sources…
        </div>
      ) : (
        <ul className="space-y-2">
          {(data ?? []).map((s: any) => (
            <li
              key={s.id}
              className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-border bg-card p-3 text-sm shadow-soft"
            >
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 font-semibold text-navy">
                  <Rss className="h-3.5 w-3.5 text-gold" /> {s.name}
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide ${
                      s.enabled
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {s.enabled ? "enabled" : "disabled"}
                  </span>
                </p>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">
                  {s.url}
                </p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Every {s.frequency_minutes} min · category{" "}
                  <span className="font-medium">{s.default_category}</span>
                  {s.last_run_at ? (
                    <>
                      {" "}
                      · last run{" "}
                      {new Date(s.last_run_at).toLocaleString("en-ZA")} ·{" "}
                      <span
                        className={
                          s.last_status === "ok"
                            ? "text-emerald-700"
                            : "text-rose-700"
                        }
                      >
                        {s.last_status ?? "—"}
                      </span>
                      {s.last_inserted != null && (
                        <> · inserted {s.last_inserted}</>
                      )}
                    </>
                  ) : (
                    <> · never run</>
                  )}
                </p>
                {s.last_error && (
                  <p className="mt-1 line-clamp-2 text-[11px] text-rose-700">
                    {s.last_error}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => startEdit(s)}
                  className="rounded-full border border-border px-3 py-1 text-[11px] font-medium hover:border-navy hover:text-navy"
                >
                  Edit
                </button>
                <button
                  onClick={() => {
                    if (confirm(`Delete RSS source "${s.name}"?`)) {
                      remove.mutate(s.id);
                    }
                  }}
                  className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-100"
                >
                  <Trash2 className="h-3 w-3" /> Delete
                </button>
              </div>
            </li>
          ))}
          {(data ?? []).length === 0 && (
            <li className="text-sm text-muted-foreground">
              No sources configured yet.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
